<template>
  <div
    ref="widgetRef"
    :class="['phone-widget', { 'expanded': isExpanded, 'has-call': hasIncomingCall, 'in-call': isInCall }]"
    :style="widgetStyle"
  >
    <!-- ==================== -->
    <!-- WIDGET COLAPSADO -->
    <!-- ==================== -->
    <div v-if="!isExpanded" :class="['widget-collapsed', phoneIconClass]">
      <!-- Área de arrastar (handle) -->
      <div class="drag-handle-collapsed" @mousedown="startDrag">
        <span class="drag-dots">⋮⋮</span>
      </div>

      <!-- Área clicável para expandir -->
      <div :class="['widget-icon', phoneIconClass]" @click="toggleExpand">
        <svg :class="['phone-icon', phoneIconClass]" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>

      <!-- Indicador de chamada entrante -->
      <div v-if="hasIncomingCall" class="incoming-indicator">
        <span class="pulse-ring"></span>
      </div>

      <!-- Timer de chamada em andamento -->
      <div v-if="isInCall" class="call-indicator">
        {{ formattedTimer }}
      </div>
    </div>

    <!-- ==================== -->
    <!-- WIDGET EXPANDIDO -->
    <!-- ==================== -->
    <div v-else class="widget-expanded" :style="widgetBackgroundStyle" @click.stop>
      <!-- Logo da unidade quando há chamada -->
      <div v-if="(hasIncomingCall || isInCall) && unitInfo.logo" class="unit-logo-header">
        <img :src="unitInfo.logo" :alt="unitInfo.name" :class="['unit-logo-img', unitInfo.key]" />
      </div>

      <!-- Header arrastável -->
      <div class="widget-header" @mousedown="startDrag">
        <div class="drag-handle">⋮⋮</div>
        <div class="header-content">
          <span class="header-title">{{ statusText }}</span>
        </div>
        <!-- CORREÇÃO: durante chamada ativa o ✕ encerra a chamada, não minimiza -->
        <button
          @click.stop="isInCall ? hangupCall() : toggleExpand()"
          :class="['btn-close', { 'btn-close-active': isInCall }]"
          :title="isInCall ? 'Encerrar chamada' : 'Minimizar'"
        >{{ isInCall ? '📵' : '✕' }}</button>
      </div>

      <!-- Conteúdo -->
      <div class="widget-content">
        <!-- CHAMADA ENTRANTE -->
        <div v-if="hasIncomingCall" class="call-section">
          <div class="call-info">
            <p class="caller-name">{{ currentCall?.callerName || 'Desconhecido' }}</p>
            <p class="caller-number">{{ currentCall?.callerNumber }}</p>
            <p class="inbox-name">{{ currentCall?.inboxName || 'Telefone' }}</p>
          </div>
          <div class="call-actions-inline">
            <button
              @click="answerCall"
              class="btn-icon btn-answer"
              :disabled="sipStatus !== 'registered' || isAnswering"
              :title="isAnswering ? 'Conectando...' : (sipStatus !== 'registered' ? 'SIP desconectado — aguarde reconectar' : 'Atender')"
            >{{ isAnswering ? '⏳' : '📞' }}</button>
            <button @click="hangupCall" class="btn-icon btn-reject">📵</button>
          </div>
        </div>

        <!-- CHAMADA EM ANDAMENTO -->
        <div v-else-if="isInCall" class="call-section">
          <div class="call-info-mini">
            <span class="call-timer">{{ formattedTimer }}</span>
            <span class="call-number">{{ currentCall?.callerNumber }}</span>
          </div>
          <!-- Indicador de qualidade de rede -->
          <div v-if="callQuality.degraded" class="quality-warning">
            ⚠️ Qualidade reduzida
          </div>
          <div class="call-controls">
            <button @click="toggleMute" :class="['btn-icon', 'btn-control', { active: isMuted }]">
              {{ isMuted ? '🔊' : '🎤' }}
            </button>
            <button @click="hangupCall" class="btn-icon btn-hangup">📞</button>
          </div>
        </div>

        <!-- ESTADO IDLE -->
        <div v-else class="idle-section">
          <p class="idle-text">
            <svg :class="['phone-icon-small', phoneIconClass]" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path :class="phoneIconClass" d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            {{ statusText }}
          </p>
        </div>

        <!-- Erro -->
        <div v-if="error" class="error-mini">
          ⚠️ {{ error }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useCallControl, getVoipUnitByInboxId } from '../composables/useCallControl';
import { useSipAudio } from '../composables/useSipAudio';
import { emitter } from 'shared/helpers/mitt';

export default {
  name: 'PhoneWidget',

  setup() {
    const store = useStore();

    // Call Control: gerencia estado e ações da chamada
    const {
      currentCall,
      isMuted,
      answerCall: answerCallControl,
      hangupCall: hangupCallControl,
      toggleMute,
      registerSipHangup,
      error: callControlError,
      isAnswering,
    } = useCallControl();

    // Extensão SIP do usuário logado: user 1 → 1000, user 2 → 1001, etc.
    const userSipExtension = computed(() => {
      const userId = store.getters.getCurrentUser?.id;
      if (!userId) return null;
      return `SIP/${999 + userId}`;
    });

    // SIP Audio: gerencia registro SIP e WebRTC
    const {
      sipStatus,
      error: sipError,
      hangup: hangupSip
    } = useSipAudio({
      onCallHangup: () => {
        // Quando o cliente desliga a ligação pelo celular,
        // precisamos limpar a chamada no Call Control
        if (currentCall.value) {
          console.log('[PhoneWidget] 📵 Cliente desligou - limpando chamada:', currentCall.value.id);
          stopTimer();
          isExpanded.value = false;
        }
      },
      sipExtension: userSipExtension // Extensão própria do usuário logado
    });

    // Registrar função hangup do SIP no Call Control
    if (registerSipHangup) {
      registerSipHangup(hangupSip);
      console.log('[PhoneWidget] ✅ Função SIP hangup registrada no Call Control');
    }

    // Estado
    const widgetRef = ref(null);
    const isExpanded = ref(false);
    const error = ref(null);
    const callTimer = ref(0);
    let timerInterval = null;

    // Qualidade da chamada (monitorada via WebRTC stats)
    const callQuality = ref({ degraded: false, fractionLost: 0, jitter: 0 });

    // Arrastar - Inicial no canto superior direito com margem para não cortar
    const position = ref({ x: window.innerWidth - 340, y: 20 });
    const isDragging = ref(false);
    const dragStart = ref({ x: 0, y: 0 });

    // Computeds
    const widgetStyle = computed(() => ({
      left: position.value.x + 'px',
      top: position.value.y + 'px'
    }));

    const hasIncomingCall = computed(() => currentCall.value && !currentCall.value.answered);
    const isInCall = computed(() => currentCall.value && currentCall.value.answered);

    const formattedTimer = computed(() => {
      const minutes = Math.floor(callTimer.value / 60);
      const seconds = callTimer.value % 60;
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    });

    const statusText = computed(() => {
      switch (sipStatus.value) {
        case 'registering':
          return 'Conectando SIP...';
        case 'registered':
          return 'Telefone Online (SIP)';
        case 'unregistered':
          return 'Telefone Offline';
        default:
          return 'Telefone';
      }
    });

    const phoneIconClass = computed(() => {
      switch (sipStatus.value) {
        case 'registering':
          return 'registering';
        case 'registered':
          return 'online';
        case 'unregistered':
          return 'offline';
        default:
          return 'offline';
      }
    });

    // Cores e logos baseadas na unidade
    const unitInfo = computed(() => {
      // 1) Unidade cadastrada no Super Admin (Voip numbers), casada pelo inboxId da chamada.
      //    key = unit_key, usado como classe da logo (.unit-logo-img.altana etc.).
      const unit = getVoipUnitByInboxId(currentCall.value?.inboxId);
      if (unit) {
        return {
          key: unit.unit_key || null,
          color: unit.color || '#192335',
          logo: unit.logo_url || null,
          name: unit.unit_name || 'Telefone'
        };
      }

      // 2) Fallback: mapeamento por nome da inbox (vale enquanto /voip/units não responder)
      const inboxName = currentCall.value?.inboxName || currentCall.value?.unit || '';
      const nameLower = inboxName.toLowerCase();

      if (nameLower.includes('lush')) {
        return {
          key: 'lush',
          color: '#00360B',
          logo: '/brands/lush/logos/logo-main.png',
          name: 'Lush Motel'
        };
      } else if (nameLower.includes('tout')) {
        return {
          key: 'tout',
          color: '#370741',
          logo: '/brands/tout/logos/logo-main.png',
          name: 'Tout'
        };
      } else if (nameLower.includes('andar de cima') || nameLower.includes('andar-de-cima')) {
        return {
          key: 'andar-de-cima',
          color: '#161D25',
          logo: '/brands/andar-de-cima/logos/logo-main.png',
          name: 'Andar de Cima'
        };
      } else if (nameLower.includes('altana')) {
        return {
          key: 'altana',
          color: '#002E09',
          logo: '/brands/altana/logos/logo-light.png',
          name: 'Altana Motel'
        };
      }

      return {
        key: null,
        color: '#192335',
        logo: null,
        name: 'Telefone'
      };
    });

    const widgetBackgroundStyle = computed(() => ({
      background: `linear-gradient(135deg, ${unitInfo.value.color} 0%, ${adjustColor(unitInfo.value.color, -20)} 100%)`
    }));

    const adjustColor = (color, percent) => {
      const num = parseInt(color.replace('#', ''), 16);
      const amt = Math.round(2.55 * percent);
      const R = (num >> 16) + amt;
      const G = (num >> 8 & 0x00FF) + amt;
      const B = (num & 0x0000FF) + amt;
      return '#' + (0x1000000 +
        (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
        (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
        (B < 255 ? B < 1 ? 0 : B : 255)
      ).toString(16).slice(1);
    };

    // Watchers
    watch(currentCall, (call) => {
      if (call && !call.answered) {
        isExpanded.value = true;
      } else if (call && call.answered) {
        startTimer();
      } else {
        stopTimer();
        // Limpar qualidade ao encerrar chamada
        callQuality.value = { degraded: false, fractionLost: 0, jitter: 0 };
      }
    });

    // Propagar erros do bridge (ex: "SIP channel timeout", "Allocation failed")
    watch(callControlError, (newError) => {
      if (newError) {
        error.value = newError;
        setTimeout(() => { error.value = null; }, 6000);
      }
    });

    // Drag functions
    const startDrag = (e) => {
      if (e.target.closest('.btn-close') || e.target.closest('button')) return;
      isDragging.value = true;
      dragStart.value = {
        x: e.clientX - position.value.x,
        y: e.clientY - position.value.y
      };
      document.addEventListener('mousemove', onDrag);
      document.addEventListener('mouseup', stopDrag);
    };

    const onDrag = (e) => {
      if (!isDragging.value) return;
      const widgetWidth = 320;
      const widgetHeight = 250;
      position.value = {
        x: Math.max(0, Math.min(window.innerWidth - widgetWidth, e.clientX - dragStart.value.x)),
        y: Math.max(0, Math.min(window.innerHeight - widgetHeight, e.clientY - dragStart.value.y))
      };
    };

    const stopDrag = () => {
      isDragging.value = false;
      document.removeEventListener('mousemove', onDrag);
      document.removeEventListener('mouseup', stopDrag);
    };

    const toggleExpand = () => {
      isExpanded.value = !isExpanded.value;
    };

    const answerCall = async () => {
      if (!currentCall.value) return;
      if (sipStatus.value !== 'registered') {
        error.value = 'SIP desconectado. Aguarde o telefone conectar e tente novamente.';
        setTimeout(() => { error.value = null; }, 5000);
        return;
      }
      console.log('[PhoneWidget] 📞 Atendendo chamada via Call Control:', currentCall.value.id);

      try {
        await answerCallControl(currentCall.value.id);
        console.log('[PhoneWidget] ✅ Chamada atendida! Áudio via asterisk-bridge');

      } catch (err) {
        console.error('[PhoneWidget] ❌ Erro ao atender:', err);
        error.value = 'Erro ao atender: ' + err.message;
      }
    };

    const hangupCall = async () => {
      if (!currentCall.value) return;
      console.log('[PhoneWidget] 📵 Desligando chamada');

      await hangupCallControl(currentCall.value.id);
      isExpanded.value = false;
    };

    const startTimer = () => {
      callTimer.value = 0;
      timerInterval = setInterval(() => callTimer.value++, 1000);
    };

    const stopTimer = () => {
      if (timerInterval) clearInterval(timerInterval);
      timerInterval = null;
      callTimer.value = 0;
    };

    // Handler de qualidade de chamada emitido pelo useSipAudio
    const onQualityUpdate = ({ fractionLost, jitter }) => {
      callQuality.value = {
        degraded: fractionLost > 0.05 || jitter > 0.05,
        fractionLost,
        jitter,
      };
    };

    // Lifecycle
    onMounted(() => {
      console.log('[PhoneWidget] ✅ Widget montado');
      emitter.on('callQualityUpdate', onQualityUpdate);
    });

    onUnmounted(() => {
      stopTimer();
      emitter.off('callQualityUpdate', onQualityUpdate);
    });

    return {
      widgetRef,
      widgetStyle,
      isExpanded,
      sipStatus,
      hasIncomingCall,
      isInCall,
      currentCall,
      isMuted,
      isAnswering,
      callTimer,
      formattedTimer,
      error,
      statusText,
      phoneIconClass,
      unitInfo,
      widgetBackgroundStyle,
      callQuality,
      toggleExpand,
      startDrag,
      answerCall,
      hangupCall,
      toggleMute
    };
  }
};
</script>

<style scoped>
.phone-widget {
  position: fixed;
  z-index: 9999;
  user-select: none;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Widget Colapsado */
.widget-collapsed {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  border: 2px solid #334155;
  border-radius: 50%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.widget-collapsed:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.5);
}

.widget-collapsed.has-call {
  border-color: #10b981;
  animation: pulse-border 1.5s infinite;
}

@keyframes pulse-border {
  0%, 100% { box-shadow: 0 4px 20px rgba(16, 185, 129, 0.4); }
  50% { box-shadow: 0 6px 30px rgba(16, 185, 129, 0.8); }
}

.widget-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: all 0.3s ease;
}

.widget-collapsed:hover.online {
  border-color: #10b981;
  box-shadow: 0 6px 25px rgba(16, 185, 129, 0.5);
}

.widget-collapsed:hover.offline {
  border-color: #ef4444;
  box-shadow: 0 6px 25px rgba(239, 68, 68, 0.5);
}

.widget-collapsed:hover.registering {
  border-color: #fbbf24;
  box-shadow: 0 6px 25px rgba(251, 191, 36, 0.5);
}

.phone-icon {
  transition: all 0.3s ease;
}

.phone-icon path {
  transition: all 0.3s ease;
}

.phone-icon.online path {
  stroke: #10b981;
}

.phone-icon.online:hover path {
  stroke: #34d399;
  filter: drop-shadow(0 0 8px #10b981);
}

.phone-icon.offline path {
  stroke: #ef4444;
}

.phone-icon.offline:hover path {
  stroke: #f87171;
  filter: drop-shadow(0 0 8px #ef4444);
}

.phone-icon.registering path {
  stroke: #fbbf24;
  animation: pulse-icon 1s infinite;
}

.phone-icon.registering:hover path {
  stroke: #fcd34d;
  filter: drop-shadow(0 0 8px #fbbf24);
}

@keyframes pulse-icon {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}

.phone-icon-small {
  transition: all 0.3s ease;
}

.phone-icon-small.online path {
  stroke: #10b981;
}

.phone-icon-small.offline path {
  stroke: #ef4444;
}

.phone-icon-small.registering path {
  stroke: #fbbf24;
  animation: pulse-icon 1s infinite;
}

.incoming-indicator {
  position: absolute;
  top: -8px;
  right: -8px;
}

.pulse-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: #10b981;
  animation: pulse-ring 1.5s infinite;
}

@keyframes pulse-ring {
  0% { transform: scale(1); opacity: 1; }
  100% { transform: scale(2); opacity: 0; }
}

.call-indicator {
  position: absolute;
  bottom: -8px;
  background: #3b82f6;
  color: white;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
  font-family: 'Courier New', monospace;
}

/* Widget Expandido */
.widget-expanded {
  width: 320px;
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  border: 1px solid #334155;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
  overflow: hidden;
  animation: expand 0.3s ease;
}

@keyframes expand {
  from { opacity: 0; transform: scale(0.9); }
  to { opacity: 1; transform: scale(1); }
}

.widget-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.2);
  border-bottom: 1px solid #334155;
  cursor: move;
}

.drag-handle {
  color: #64748b;
  font-size: 16px;
  cursor: grab;
  padding: 4px;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.header-title {
  color: #f1f5f9;
  font-size: 14px;
  font-weight: 600;
}

.btn-close {
  background: rgba(239, 68, 68, 0.2);
  border: none;
  color: #ef4444;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.btn-close:hover {
  background: #ef4444;
  color: white;
}

/* CORREÇÃO: estilo diferenciado quando há chamada ativa */
.btn-close.btn-close-active {
  background: rgba(239, 68, 68, 0.4);
  border: 1px solid #ef4444;
  color: white;
  width: 32px;
  height: 32px;
  font-size: 18px;
}

.btn-close.btn-close-active:hover {
  background: #ef4444;
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.5);
}

.widget-content {
  padding: 16px;
}

.call-section {
  text-align: center;
}

.call-info {
  margin-bottom: 16px;
}

.caller-name {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #f1f5f9;
}

.caller-number {
  margin: 4px 0;
  font-size: 14px;
  color: #94a3b8;
}

.inbox-name {
  margin: 0;
  font-size: 12px;
  color: #64748b;
}

.call-info-mini {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  padding: 12px;
  background: rgba(59, 130, 246, 0.1);
  border-radius: 10px;
}

.call-timer {
  font-size: 20px;
  font-weight: 700;
  color: #3b82f6;
  font-family: 'Courier New', monospace;
}

.call-number {
  font-size: 14px;
  color: #94a3b8;
}

.quality-warning {
  background: rgba(251, 191, 36, 0.15);
  border: 1px solid rgba(251, 191, 36, 0.4);
  border-radius: 6px;
  color: #fbbf24;
  font-size: 12px;
  padding: 4px 8px;
  margin-bottom: 10px;
  text-align: center;
}

.call-actions-inline,
.call-controls {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.btn-icon {
  width: 50px;
  height: 50px;
  border: none;
  border-radius: 12px;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-answer {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.btn-answer:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.5);
}

.btn-answer:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.btn-reject {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
}

.btn-reject:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 15px rgba(239, 68, 68, 0.5);
}

.btn-hangup {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
}

.btn-hangup:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 15px rgba(239, 68, 68, 0.5);
}

.btn-control {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

.btn-control:hover {
  background: rgba(255, 255, 255, 0.2);
}

.btn-control.active {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: #1e293b;
}

.idle-section {
  text-align: center;
  padding: 20px 0;
}

.idle-text {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0;
  color: #94a3b8;
  font-size: 14px;
}

.error-mini {
  margin-top: 12px;
  padding: 8px 12px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  color: #f87171;
  font-size: 13px;
}

.unit-logo-header {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.unit-logo-img {
  width: 120px;
  height: 120px;
  object-fit: contain;
}

/* Logo do Andar de Cima maior no widget expandido */
.unit-logo-img.andar-de-cima {
  width: 140px;
  height: 140px;
}

/* Versão lilás da logo (logo-light.png): a original é verde escura e sumiria
   no fundo verde do widget */
.unit-logo-img.altana {
  width: 150px;
  height: auto;
}
</style>
